import { condenseCurrency } from '@shopify/condense-number';
import { CurrencyCode, CurrencySymbol, CURRENCY_MAP } from './currencies';

/**
 * Condense a monetary value based on the currency
 * @param currency Defaults to 'USD'
 * @param options Defaults to the 'en' locale, and
 * a max precision of 0
 */
export function condenseMonetaryValue(
  value: number,
  currency?: string | null,
  options?: {
    locale?: string | null;
    maxPrecision?: number | null;
    roundingRule?: 'auto' | 'up'; // If not specified, rounds down
  }
): string {
  const currencyCode = currency || 'USD';
  const locale = options?.locale || 'en';

  return condenseCurrency(value, locale, currencyCode, {
    ...(options?.maxPrecision ? { maxPrecision: options?.maxPrecision } : {}),
    ...(options?.roundingRule ? { roundingRule: options?.roundingRule } : {}),
  });
}

/**
 * Format revenue per million. Defaults to displaying currency in USD
 */
export function condenseMoneyPerMillion(currency?: string | null): string {
  return condenseMonetaryValue(1_000_000, currency);
}

/**
 * Format a monetary value for display. Note that unlike condenseMonetaryValue,
 * this does not do any condensing.
 */
export function formatMonetaryValue(
  value: number | string,
  currency?: string | null,
  options?: {
    minimumFractionDigits: number;
    maximumFractionDigits: number;
  }
): string {
  const parsedValue = typeof value === 'string' ? parseFloat(value) : value;
  return parsedValue.toLocaleString('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    ...options,
  });
}

export function isCurrencyCode(
  currency: string | null
): currency is CurrencyCode {
  return currency !== null && currency in CURRENCY_MAP;
}

/**
 * Given a currency code (eg. 'GBP'), return the currency symbol (eg. '£').
 * If the currency code is not recognized, returns the currency code.
 */
export function getCurrencySymbol(
  currency: string | null
): CurrencySymbol | string {
  if (currency === null) {
    return '$';
  } else if (isCurrencyCode(currency)) {
    return CURRENCY_MAP[currency].symbol;
  } else {
    return currency;
  }
}
